// ---------------------------------------------------------------------------
// Brainstorm Provider — singleton factory with env-flag selection.
//
// VITE_BRAINSTORM_PROVIDER=real  → createRealBrainstormProvider({ kg })
// VITE_BRAINSTORM_PROVIDER=mock  → createMockBrainstormProvider() (default)
//
// Real provider needs a `CogoKnowledgeRepository` instance; KgProvider's
// readiness effect calls `setBrainstormDeps({ kg })` once the repo is wired.
// Until then `getBrainstormProvider()` returns the mock (when flag=mock) or
// a real provider with `kg=null` (which yields empty findCandidates results
// rather than throwing — see real.ts).
// ---------------------------------------------------------------------------

import type { CogoKnowledgeRepository } from '@cogo/kg-repository'
import type { BrainstormRepository } from '../repository'
import { createMockBrainstormProvider } from './mock'
import { createRealBrainstormProvider } from './real'

let instance: BrainstormRepository | null = null
const deps: { kg: CogoKnowledgeRepository | null } = { kg: null }

/**
 * Inject the Repository the real provider needs. Called by KgProvider's
 * readiness effect AFTER `kg.ready()` resolves. Idempotent — second call
 * with the same kg is a no-op; second call with a different kg drops the
 * cached singleton so subsequent `getBrainstormProvider()` constructs a
 * fresh provider bound to the new kg.
 */
export function setBrainstormDeps(next: {
  kg: CogoKnowledgeRepository | null
}): void {
  if (deps.kg === next.kg) return
  deps.kg = next.kg
  instance = null // re-construct on next get with the new kg
}

export function getBrainstormProvider(): BrainstormRepository {
  if (instance) return instance
  const flag = import.meta.env.VITE_BRAINSTORM_PROVIDER ?? 'mock'
  instance =
    flag === 'real'
      ? createRealBrainstormProvider({ kg: deps.kg })
      : createMockBrainstormProvider()
  return instance
}

export function __resetBrainstormProviderForTests(): void {
  instance = null
  deps.kg = null
}
